import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { SlidePanel } from '@/components/SlidePanel'
import { addOrganizationRole } from '@/services/organization.service'
import { toast } from '@/utils/helpers'

// Organizations only have Owner/Admin roles (see ORGANIZATION_ROLES).
const ORG_ROLE_OPTIONS = ['Owner', 'Admin']

const AddUserSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  role: z.enum(ORG_ROLE_OPTIONS),
})

const DEFAULT_VALUES = {
  email: '',
  firstName: '',
  lastName: '',
  role: 'Admin',
}

/**
 * Add a member to an organization (RSCX-320). Grants an org-level role
 * (Owner/Admin); brand-new users are created and emailed an invite. Facility
 * access is managed afterward from the row's Manage Access panel.
 */
const OrganizationAddUserSlideout = ({ organizationId, resetAndClose }) => {
  const queryClient = useQueryClient()
  const form = useForm({
    resolver: zodResolver(AddUserSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  })

  const resetForm = () => {
    form.reset(DEFAULT_VALUES)
    resetAndClose()
  }

  const { isPending, mutate: addMember } = useMutation({
    mutationFn: (data) =>
      addOrganizationRole({
        organizationId,
        newUserData: { email: data.email, firstName: data.firstName, lastName: data.lastName },
        roles: [data.role],
      }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Member added', 'success')
      queryClient.invalidateQueries({ queryKey: ['organization-users', organizationId] })
      queryClient.invalidateQueries({ queryKey: ['organization-roles', organizationId] })
      resetForm()
    },
  })

  return (
    <SlidePanel
      actions={[
        { label: 'Cancel', onClick: () => resetAndClose(), type: 'cancel' },
        { label: 'Add User', onClick: form.handleSubmit(addMember), type: 'submit' },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(addMember)}>
            <div className="flex flex-col gap-4">
              <span className="leading-6 font-bold">User Details</span>

              <FormField
                control={form.control}
                disabled={isPending}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="person@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-row gap-4">
                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <span className="leading-6 font-bold">Organization Role</span>
              <FormField
                control={form.control}
                disabled={isPending}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isPending}
                    >
                      <FormControl>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ORG_ROLE_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      Owners and Admins can manage the organization and have full access to every
                      facility. You can fine-tune facility access after adding them.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
      }
      loading={isPending}
      title="Add User"
      setOpen={resetForm}
    />
  )
}

OrganizationAddUserSlideout.propTypes = {
  organizationId: PropTypes.string.isRequired,
  resetAndClose: PropTypes.func.isRequired,
}

export default OrganizationAddUserSlideout
